import { makeAutoObservable, observable, reaction, runInAction, values } from 'mobx';
import axios from 'axios';

import { logger } from '@/lib/api/logger';
import {
  getIntervalsByDiscipline,
  getScheduleByDid,
  getStatisticsEventsByDid,
} from '@/lib/api/statistic';
import { mobx } from '@/lib/mobx';
import { dateHelper } from '@/helpers/dateHelper';
import { getFormattedSlug } from '@/helpers/getFormattedSlug';
import { isToday } from '@/helpers/isToday';
import { LS } from '@/helpers/localStorageHelper';
import { sortBy } from '@/helpers/sortBy';
import { uniq } from '@/helpers/uniq';
import { uniqBy } from '@/helpers/uniqBy';
import { isShadowMode, shadowModeParams } from '@/config';
import {
  customTournamentPageId,
  customTournamentPageOutrightIds,
  customTournamentPageSlug,
  disciplineIds as disciplineIdsFromConstants,
  disciplinesWithChat,
  entityNames,
  fullLine,
  internationalTournamentsIds,
  justForYouBlockTournamentIds,
  liveFullLine,
  livePrematchLine,
  minTopIndexForTopEvents,
  prematchFullLine,
  socketBatchActions,
  subscribeChain,
  topCountriesByCids,
  topEventsLine,
  topEventsLines,
} from '@/config/constants';

import { Discipline } from './Discipline.mobx';
import { Event } from './Event.mobx';
import { Line } from './Line.mobx';
import { Market } from './Market.mobx';
import { Tournament } from './Tournament.mobx';

/**
 * @template K
 * @template V
 * @typedef {import('mobx').ObservableMap<K, V>} ObservableMap
 */

const _MS_PER_DAY = 1000 * 60 * 60 * 24;

// a and b are javascript Date objects
function dateDiffInDays(a, b) {
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

function getName(name) {
  return `${name}s`;
}

const models = {
  disciplines: Discipline,
  events: Event,
  lines: Line,
  markets: Market,
  tournaments: Tournament,
};

/**
 * @typedef {import('./types').CountryTournamentsItem} CountryTournamentsItem
 * @typedef {import('./types').CountryTournaments} CountryTournaments
 * @typedef {import('./types').LiveDisciplineWithEvents} LiveDisciplineWithEvents
 * @typedef {import('./types').TopDisciplineWithEvents} TopDisciplineWithEvents
 */

export class StoreMobxMain {
  constructor(root, initialState = {}) {
    makeAutoObservable(this);

    this.root = root;

    this.getAssets();

    Object.assign(
      this,
      Object.keys(models).reduce(
        (acc, entity) => ({
          ...acc,
          [entity]: observable.map(initialState[entity] || {}),
        }),
        {},
      ),
    );

    this.entitiesWithoutSubscription = new Map();

    this.clearStorageTimeout = null;

    // TODO: TMP for testing
    this.clearStorageSettingsLS = JSON.parse(LS.get('clearStoreSettings')) || {};

    this.isTimeToClearStorage = this.clearStorageSettingsLS.differentModelClearMode || false;

    reaction(
      () => this.counts,
      (counts) => {
        const { disciplineIds, disciplineIdsAliases } = this.root.ui;
        const disciplineIdsSet = new Set(disciplineIds);
        const disciplinesByIds = new Map();

        this.lobbyDisciplinesList = Object.entries(counts)
          .reduce((acc, [id, data]) => {
            if (!disciplineIdsSet.has(id)) {
              return acc;
            }
            const { live = 0, prematch = 0 } = data;
            const hasEvents = live + prematch > 0;
            data.id = id;

            // Ignore merging of disciplines for lobbyDisciplinesByIds Map
            if (hasEvents) {
              disciplinesByIds.set(id, data);
            }

            if (disciplineIdsAliases[id] !== id || !disciplineIdsFromConstants.includes(id)) {
              // CASE when main discipline is empty
              const alias = counts[disciplineIdsAliases[id]];

              if (alias && alias.live + alias.prematch === 0 && hasEvents) {
                alias.name = alias.name || alias.id;
                acc.push(alias);
              }
              return acc;
            }

            if (hasEvents) {
              acc.push(data);
            }

            return acc;
          }, [])
          .sort((a, b) => disciplineIds.indexOf(a.id) - disciplineIds.indexOf(b.id));

        this.lobbyDisciplinesByIds = disciplinesByIds;
      },
    );

    reaction(
      () => this.eventRefs,
      (eventRefs) => {
        this.eventRefsByIds = eventRefs.reduce((acc, eventRef) => {
          acc[eventRef.lid] = eventRef;
          return acc;
        }, {});
      },
    );
  }

  /** @type {ObservableMap<string, Line>} */
  lines = new Map();

  /** @type {ObservableMap<string, Discipline>} */
  disciplines = new Map();

  /** @type {ObservableMap<string, EN.Tournament>} */
  tournaments = new Map();

  /** @type {ObservableMap<string, EN.Event>} */
  events = new Map();

  /** @type {ObservableMap<string, Market>} */
  markets = new Map();

  /**
   * @description - Serves to understand whether there are entities
   * without a websocket subscription, in order to avoid unnecessary calculations.
   * @type {Number}
   */
  numOfEntitiesWithoutSubscription = 0;

  lobbyDisciplinesList = [];

  lobbyDisciplinesByIds = new Map();

  eventRefsByIds = {};

  assets = null;

  /**
   * @type {Map<string, EN.Scoreboard['scoreboard']>}
   */
  scoreboards = new Map();

  /**
   * @type {string[]}
   */
  scoreboardSubs = [];

  /**
   * @type {Map<string, EN.Scoreboard['timer']>}
   */
  timers = new Map();

  /**
   * @type {Map<string, EN.Scoreboard['server']>}
   */
  servings = new Map();

  /**
   * @type {Map<string, EN.Scoreboard['stage']>}
   */
  stages = new Map();

  scheduleDisciplines = [];

  scheduleEvents = { notReady: true };

  isScheduleEventsLoading = false;

  slides = [];

  worldCupFinishedEvents = [];

  slugEventId = '';

  lineId = '';

  getItemByLIDmodelName = '';

  getItemByLIDid = '';

  getOutcomeByLIDid = '';

  counts = {};

  liveDisciplinesFilters = JSON.parse(LS.get('liveDisciplinesFilters')) || [];

  topPageDisciplinesFilters = JSON.parse(LS.get('TPDF')) || [];

  topDisciplinesFilters = [];

  marketsIntervals = {};

  loadingMarketsIntervalPromise = null;

  isMarketsIntervalsMode = false;

  justForYouEvents = [];

  /**
   * A map to keep track of subscribed widgets.
   * @type {Set<string>}
   */
  subscribedWidgets = new Set();

  // TODO: TMP - for testing
  lazyParams = JSON.parse(LS.get('lazyParams')) || [];

  // If true, the application renders grid events in stages as the page scrolls.
  // Subscribing to events in this case is not lazy.
  isSteppedGridEventsRender = this.lazyParams.includes('stepped');

  get getCounts() {
    const { disciplineIdsAliases } = this.root.ui;
    const counts = Object.entries(this.counts).reduce(
      (acc, [id, { live, prematch }]) => {
        if (live) {
          acc.liveDisciplines = {
            ...acc.liveDisciplines,
            [disciplineIdsAliases[id]]: (acc.liveDisciplines[disciplineIdsAliases[id]] || 0) + live,
          };
        }

        return {
          ...acc,
          live: (acc.live += live),
          [disciplineIdsAliases[id]]: (acc[disciplineIdsAliases[id]] || 0) + live + prematch,
        };
      },
      {
        live: 0,
        liveDisciplines: {},
      },
    );
    return counts;
  }

  get linesValues() {
    return fullLine.map((id) => this.lines.get(id)).filter(Boolean);
  }

  get eventsValues() {
    return values(this.events);
  }

  get disciplinesValues() {
    return values(this.disciplines);
  }

  get tournamentsValues() {
    const tournaments = values(this.tournaments).sort((a, b) => b.sortIndex - a.sortIndex);

    return tournaments;
  }

  /**
   * @returns {Array<EN.EventRef & {pid: string}>}
   */
  get eventRefs() {
    return values(this.tournaments)
      .reduce(
        (acc, { eventRefs, lid }) => {
          eventRefs.forEach((event) => {
            const currentEvent = { ...event, pid: lid };

            acc.push(currentEvent);
          });
          return acc;
        },
        /** @type {Array<EN.EventRef & {pid: string}>} */ ([]),
      )
      .sort((a, b) => b.topIndex - a.topIndex);
  }

  get topEventRefsByTime() {
    // Thank you Sviat for opening my eyes.
    // By the way, there are so many more like this in this store... TODO: fix them all.
    return this.sortedTopEventsRefs.slice(0).sort((a, b) => {
      if (a.topIndex === b.topIndex) {
        return a.startTime - b.startTime;
      }

      return b.topIndex - a.topIndex;
    });
  }

  get topEventRefsByDisciplines() {
    const topEvents = {
      topEventsCount: 0,
      topPageDisciplines: {},
    };

    const eventRefsLength = this.eventRefs.length;

    if (!eventRefsLength) {
      return topEvents;
    }

    const disciplines = this.topDisciplines.map(({ id }) => id);
    const eventsIds = {};

    let index = 0;
    while (this.eventRefs[index]?.topIndex > minTopIndexForTopEvents && index <= eventRefsLength) {
      const event = this.eventRefs[index];
      const [line, did] = event.pid.split('.');
      const [_, id] = event.lid.split('.');

      index += 1;

      if (!disciplines.includes(did)) {
        continue;
      }

      if (!topEvents.topPageDisciplines[did]) {
        const { name: disciplineName } = this.disciplines.get(`${line}.${did}`) || {};

        topEvents.topPageDisciplines[did] = {
          count: 1,
          disciplineName,
          events: [event],
        };
      } else if (!eventsIds[id]) {
        const currentDiscipline = topEvents.topPageDisciplines[did];
        currentDiscipline.count += 1;
        currentDiscipline.events.push(event);
      }

      if (!eventsIds[id]) {
        topEvents.topEventsCount += 1;
      }

      eventsIds[id] = true;
    }

    return topEvents;
  }

  get marketsValues() {
    return values(this.markets);
  }

  get disciplinesOrSports() {
    const { disciplineIds } = this.root.ui;
    return disciplineIds;
  }

  get disciplinesFilter() {
    const { correctDisciplines } = this;
    const { disciplines } = this.root.ui.lobbyFilters;
    return disciplines.filter((d) => correctDisciplines.includes(d));
  }

  get actualDisciplines() {
    const { disciplines } = this.root.ui.lobbyFilters;
    const { correctDisciplines } = this;
    return disciplines.filter((d) => correctDisciplines.includes(d));
  }

  get actualDiscipline() {
    const disciplineId = this.actualDisciplines[0] || null;
    if (!disciplineId) return null;

    return this.disciplinesValues.find(({ id }) => id === disciplineId) || null;
  }

  get disciplinesNames() {
    const { disciplinesValues } = this;
    if (!disciplinesValues.length) return [];
    const { disciplinesIsSport } = this.root.ui;
    const type = disciplinesIsSport ? 2 : 1;
    const filterByType = disciplinesValues.filter(({ category }) => category === type);
    return [...new Set(filterByType.map(({ id }) => id))];
  }

  get disciplinesNamesByIds() {
    return this.disciplinesValues.reduce(
      (acc, { id, name, shortName }) => ({ ...acc, [id]: { name, shortName } }),
      {},
    );
  }

  get topTournaments() {
    const tournamentsIds = {};
    return this.tournamentsValues.reduce((acc, tournament) => {
      const { lid, id } = tournament;
      const [line] = lid.split('.');

      if (!topEventsLine.includes(line) || tournamentsIds[id]) {
        return acc;
      }

      tournamentsIds[id] = true;
      acc.push(tournament);
      return acc;
    }, []);
  }

  get favoriteTournaments() {
    const { favorite } = this.root.profile;
    const favoriteTournaments = favorite.tournaments || [];
    const tournamentIds = {};
    const favoriteTournamentsIds = favoriteTournaments.reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});

    const sortedByDiscipline = this.tournamentsValues.reduce((acc, tournament) => {
      const { id, disciplineId, lid } = tournament;
      const [line] = lid.split('.');

      if (!favoriteTournamentsIds[id] || topEventsLine.includes(line)) {
        return acc;
      }

      if (tournamentIds[id]) {
        return acc;
      }

      const currentDiscipline = this.lobbyDisciplinesList.find(({ id }) => disciplineId === id);
      const disciplineIndex = this.lobbyDisciplinesList.indexOf(currentDiscipline);

      if (acc[disciplineIndex]) {
        acc[disciplineIndex].push(tournament);
      } else {
        acc[disciplineIndex] = [tournament];
      }

      tournamentIds[id] = true;
      return acc;
    }, {});

    return Object.values(sortedByDiscipline).flat();
  }

  getAssets() {
    const filePath = `${isShadowMode ? shadowModeParams.host : ''}/assets-manifest.json`;
    axios
      .get(filePath)
      .then((res) => res.data)
      .then((data) => {
        if (data) {
          runInAction(() => {
            let currentAssets = data;
            if (isShadowMode) {
              currentAssets = Object.keys(data).reduce((acc, key) => {
                acc[key] = `${shadowModeParams.host}${data[key]}`;
                return acc;
              }, {});
            }
            this.assets = currentAssets;
          });
        }
      });
  }

  get liveEvents() {
    const { disciplineIds } = this.root.ui;
    return this.eventsValues.filter((event) => {
      const [line, discipline] = event.pid.split('.');
      return line === '2' && disciplineIds.some((el) => el === discipline);
    });
  }

  get topEvents() {
    const { disciplineIds } = this.root.ui;
    return this.eventsValues.filter((event) => {
      const [line, discipline] = event.pid.split('.');
      return (line === '4' || line === '5') && disciplineIds.some((el) => el === discipline);
    });
  }

  /**
   * @returns {{[disciplineId: string]: Tournament[]}}
   */
  get lobbyTournamentsByDisciplines() {
    const addedTournamentIds = {};
    return this.tournamentsValues.reduce(
      (acc, tournament) => {
        const { disciplineId, id } = tournament;

        if (!acc[disciplineId]) {
          acc[disciplineId] = [];
        }

        if (!addedTournamentIds[id]) {
          addedTournamentIds[id] = true;
          acc[disciplineId].push(tournament);
        }
        return acc;
      },
      /** @type {{[disciplineId: string]: Tournament[]}} */ ({}),
    );
  }

  get topTierTournamentsByDisciplines() {
    return this.correctDisciplines.reduce((acc, did) => {
      const tournamentsByDid = this.lobbyTournamentsByDisciplines[did];
      if (tournamentsByDid) {
        acc[did] = tournamentsByDid;
      }
      return acc;
    }, {});
  }

  get correctDisciplines() {
    return this.lobbyDisciplinesList.map(({ id }) => id);
  }

  get disciplinesList() {
    const { disciplinesValues } = this;
    if (!disciplinesValues.length) return [];
    const { disciplinesOrSports } = this;
    const names = {};

    const filteredDisciplinesValues = disciplinesValues.filter(({ id }) => {
      if (!names[id]) {
        names[id] = true;
        return disciplinesOrSports.includes(id);
      }
      return false;
    });
    return sortBy(filteredDisciplinesValues, 'sortIndex');
  }

  get actual() {
    const { disciplinesValues, tournaments, events, eventsValues } = this;
    const esports = disciplinesValues.filter(({ category }) => category === 1);
    const sports = disciplinesValues.filter(({ category }) => category === 2);

    const esportsEvents = esports.reduce(
      (acc, { tournamentIds = [] }) => [
        ...acc,
        ...tournamentIds.reduce((a, tid) => {
          const { eventIds = [] } = tournaments.get(tid) || {};
          return [
            ...a,
            ...eventIds
              .map((eid) => events.get(eid))
              .filter(Boolean)
              .filter(({ hasVideo, lid }) => lid.includes('2.') && hasVideo),
          ];
        }, []),
      ],
      [],
    );

    const sportsEvents = sports.reduce(
      (acc, { tournamentIds = [] }) => [
        ...acc,
        ...tournamentIds.reduce((a, tid) => {
          const { eventIds = [] } = tournaments.get(tid) || {};
          return [
            ...a,
            ...eventIds
              .map((eid) => events.get(eid))
              .filter(Boolean)
              .filter(({ hasVideo, lid }) => lid.includes('2.') && hasVideo),
          ];
        }, []),
      ],
      [],
    );

    const disciplinesWithVideos = [
      ...sportsEvents.map(({ did }) => did),
      ...esportsEvents.map(({ did }) => did),
    ].filter((v, i, a) => a.indexOf(v) === i);

    const disciplines = disciplinesValues
      .filter(({ id }) => disciplinesWithVideos.includes(id))
      .reduce((acc, d) => {
        const { shortName, id } = d;
        if (!acc[id]) {
          acc[id] = { items: [], order: 0, text: shortName };
        }

        acc[id].items = [...acc[id].items, d];

        const hasLive = acc[id].items.some(({ lid }) => lid.includes('2.'));
        acc[id].hasLive = hasLive;

        const firstEvent = eventsValues.reduce(
          (a, e) => (e.did === id && e.startTime < a.startTime ? e : a),
          { startTime: +Infinity },
        );
        acc[id].id = id;
        acc[id].sub = firstEvent.name || '';
        acc[id].firstTime = hasLive ? 0 : firstEvent.startTime;
        return acc;
      }, {});

    return Object.values(disciplines);
  }

  activeEventIsOver = false;

  /** @type {string | null} */
  activeEventSelected = null;

  /** @type {{[lid: string]: string}} - lid: lid event mapping */
  activeEventLids = {};

  /** @type {EN.Event | {}} */
  activeEventData = {};

  activeDisciplineSelected = null;

  setActiveEventIsOver = (bool = true) => {
    this.activeEventIsOver = bool;
  };

  setACtiveDiscipline = (id) => {
    this.activeDisciplineSelected = id;
  };

  /**
   * @param {EN.Event | {}} event
   */
  setActiveEventData = (event) => {
    this.activeEventData = event;
  };

  /**
   * @param {string} eventId
   */
  setActiveEvent = (eventId) => {
    this.activeEventSelected = eventId;
    this.activeEventLids = livePrematchLine.reduce((acc, line) => {
      const lid = `${line}.${eventId}`;
      acc[lid] = lid;
      return acc;
    }, {});
  };

  get activeEventSettings() {
    const settings = {
      hasChat: false,
      hasMarkets: true,
    };
    const event =
      this.events.get(`2.${this.activeEvent}`) || this.events.get(`1.${this.activeEvent}`);

    if (event) {
      settings.hasMarkets = !!event.marketIds.length;
      settings.hasChat =
        event.hasVideo && disciplinesWithChat.includes(event.did) && this.root.ui.pageWithChat;
    }
    return settings;
  }

  get activeDiscipline() {
    if (this.activeDisciplineSelected) {
      return this.activeDisciplineSelected;
    }
    if (this.actualLives && this.actualLives[0]) {
      return this.actualLives[0].id;
    }
    return null;
  }

  get activeEvent() {
    return this.activeEventSelected;
  }

  get actualLives() {
    const { fifa, rest } = this.actual.reduce(
      (acc, item) => {
        if (!item.hasLive) return acc;
        if (item.id === 'fifa') {
          acc.fifa = item;
        } else {
          acc.rest = [item, ...acc.rest];
        }
        return acc;
      },
      { fifa: {}, rest: [] },
    );
    if (fifa.id) {
      return [fifa, ...rest];
    }
    return rest;
  }

  get eventPageData() {
    const { events, slugEventId } = this;
    const event = livePrematchLine.some((id) => events.get(`${id}.${slugEventId}`));

    if (event) {
      const { pid: tournament, lid: eventId } = event;
      if (tournament && tournament.eventIds.find(({ lid }) => lid === eventId)) {
        const { pid: discipline } = tournament;
        const { pid: line } = discipline;
        return {
          discipline,
          disciplines: line ? line.disciplineIds : [],
          event,
          tournament,
        };
      }
      return {
        discipline: {},
        disciplines: [],
        event,
        tournament: {},
      };
    }
    return {
      discipline: {},
      disciplines: [],
      event: {},
      tournament: {},
    };
  }

  get getItemByLID() {
    console.warn('getItemByLID deprecated method');

    const { getItemByLIDmodelName, getItemByLIDid } = this;
    return this[getItemByLIDmodelName].get(getItemByLIDid);
  }

  set getItemByLID({ model, id }) {
    this.getItemByLIDmodelName = model;
    this.getItemByLIDid = id;
  }

  getOutcomeByLID = (id, marketId) => {
    const market = this.markets.get(marketId);
    if (market) {
      return market.outcomes.find(({ id: outcomeId }) => outcomeId === id);
    }
    return undefined;
  };

  get willBeLiveEvents() {
    const today = Math.floor(+new Date() / 1000);
    return this.eventsValues.filter(({ willBeLive, lid, pid, startTime }) => {
      if (willBeLive && lid.includes('1.')) {
        const tournament = this.tournaments.get(pid) || {};

        const {
          disciplineId,
          eventIds = [],
          lid: tLid,
          pid: { tournamentIds = [] } = {},
        } = tournament;
        return this.correctDisciplines.includes(disciplineId) && startTime > today;
      }
      return false;
    });
  }

  get willBeLiveDisciplines() {
    if (!this.willBeLiveEvents.length) return [];
    return Object.keys(
      this.willBeLiveEvents.reduce(
        (acc, { pid }) => ({
          ...acc,
          [pid.split('.')[1]]: 0,
        }),
        {},
      ),
    );
  }

  get eventsWithVideos() {
    return this.eventsValues.filter(({ hasVideo, lid, pid }) => {
      const { eventIds = [], lid: tLid, pid: tPid } = this.tournaments.get(pid) || {};
      const { tournamentIds = [] } = this.disciplines.get(tPid) || {};
      return (
        lid.includes('2.') &&
        hasVideo &&
        eventIds.some((id) => id === lid) &&
        tournamentIds.some((id) => id === tLid)
      );
    });
  }

  sortTournamentsByCountries = (a, b) => {
    const aCode = a.tournament?.region?.code?.toUpperCase();
    const aName = a.tournament?.name?.toUpperCase();
    const bCode = b.tournament?.region?.code?.toUpperCase();
    const bName = b.tournament?.name?.toUpperCase();
    if (aCode < bCode) return -1;
    if (aCode > bCode) return 1;
    if (aName < bName) return -1;
    if (aName > bName) return 1;
    return 0;
  };

  sortTournaments = (tournamentsArray) => {
    const isBrand = topCountriesByCids.find(({ brand }) => brand === this.root.auth.brand);
    if (isBrand) {
      const { countriesCode } = isBrand;
      const { selectedCountry, otherCountries } = tournamentsArray.reduce(
        (acc, el) => {
          const countryCode = el.tournament?.region?.code.toUpperCase();
          countriesCode.includes(countryCode)
            ? acc.selectedCountry.push(el)
            : acc.otherCountries.push(el);
          return acc;
        },
        { otherCountries: [], selectedCountry: [] },
      );

      return [
        ...selectedCountry.sort(this.sortTournamentsByCountries),
        ...otherCountries.sort(this.sortTournamentsByCountries),
      ];
    }
    return tournamentsArray.sort(this.sortTournamentsByCountries);
  };

  getScheduleEventsByTournaments = (scheduleEvents = []) =>
    scheduleEvents.reduce((acc, el) => {
      const { trackNumber } = el.metadata;
      if (!trackNumber) return acc;

      const eventId = el._id || el.id;
      const eventFromStore = this.events.get(`1.${eventId}`);
      const isScheduleEvent = !eventFromStore;
      const currentEvent = !isScheduleEvent ? { ...eventFromStore } : el;

      const eventDate = currentEvent?.date ? currentEvent.date : 0;
      const startTime =
        typeof eventDate === 'string' ? new Date(eventDate).getTime() : eventDate * 1000;
      const isWillBeLive = startTime > new Date().getTime();

      if (isScheduleEvent && isWillBeLive) return acc;

      const tournamentName = currentEvent.name.replace(/\s+R\d+/, '');
      const eventState = isScheduleEvent ? el.state : 'WBS';
      const tournament = isScheduleEvent ? el.tournament : this.tournaments.get(currentEvent.pid);

      const currentEventPid = currentEvent.pid || '';
      const currentEventTournament = currentEvent.tournament || {};
      const tournamentId = currentEventTournament._id || currentEventPid.split('.')[2];

      currentEvent.state = eventState;
      currentEvent.eventId = eventId;
      currentEvent.tournamentId = tournamentId;
      currentEvent.date = isScheduleEvent ? el.date : currentEvent.startTime;

      if (acc[tournamentName]) {
        acc[tournamentName].events[trackNumber] = currentEvent;
      } else {
        acc[tournamentName] = {
          events: {
            [trackNumber]: currentEvent,
          },
          tournament,
          tournamentId,
          tournamentName,
        };
      }

      return acc;
    }, {});

  setScheduleEventsByDate = async (date, fetchAttemptCount, tournamentId) => {
    const fetchAttemptNum = fetchAttemptCount || 1;
    const attemptsMaxNum = 3;
    const secondsOfDay = 60 * 60 * 24;
    this.isScheduleEventsLoading = true;

    const {
      lobbyFilters: { disciplines },
      language,
    } = this.root.ui;
    const [did] = disciplines;

    const todayDate = new Date();
    const todayTimestamp = dateHelper(todayDate.getTime() / 1000).dateRoundUpToMidnight.getTime();
    const selectedDateFull = dateHelper(date / 1000).dateFullReverse;
    const selectedNextDateFull = dateHelper(date / 1000 + secondsOfDay).dateFullReverse;
    const selectedPrevDateFull = dateHelper(date / 1000 - secondsOfDay).dateFullReverse;
    const dateTo = new Date(date);
    dateTo.setDate(dateTo.getDate() + 1);

    try {
      const scheduleData = await getScheduleByDid(did, {
        dateFrom: date - secondsOfDay * 1000,
        dateTo: dateTo.getTime(),
        locale: language,
        // pageNumber: 1,
        pageSize: 500,
        tournamentId,
      });

      const { todayEventsById, nextEventsById } = this.eventsValues.reduce(
        (acc, ev) => {
          const eventDateFull = dateHelper(ev.startTime).dateFullReverse;
          if (ev.did === did) {
            const idEvent = ev.pid.split('.')[2];
            if (selectedDateFull === eventDateFull) {
              acc.todayEventsById[idEvent]
                ? acc.todayEventsById[idEvent].push(ev)
                : (acc.todayEventsById[idEvent] = [ev]);
            }
            if (selectedNextDateFull === eventDateFull) {
              acc.nextEventsById[idEvent]
                ? acc.nextEventsById[idEvent].push(ev)
                : (acc.nextEventsById[idEvent] = [ev]);
            }
          }
          return acc;
        },
        { nextEventsById: {}, todayEventsById: {} },
      );

      const { todayApiEventsById, prevApiEventsById } = scheduleData.data.data.reduce(
        (acc, ev) => {
          const eventDateFull = dateHelper(new Date(ev.date).getTime() / 1000).dateFullReverse;
          const idEvent = ev.tournament._id;
          if (selectedDateFull === eventDateFull) {
            acc.todayApiEventsById[idEvent]
              ? acc.todayApiEventsById[idEvent].push(ev)
              : (acc.todayApiEventsById[idEvent] = [ev]);
          }
          if (selectedPrevDateFull === eventDateFull) {
            acc.prevApiEventsById[idEvent]
              ? acc.prevApiEventsById[idEvent].push(ev)
              : (acc.prevApiEventsById[idEvent] = [ev]);
          }
          return acc;
        },
        { prevApiEventsById: {}, todayApiEventsById: {} },
      );

      const eventsId = uniq([
        ...Object.keys(todayEventsById),
        ...Object.keys(todayApiEventsById),
      ]).reduce((acc, key) => {
        const storeEvents = todayEventsById[key] || [];
        const apiEvents = todayApiEventsById[key] || [];

        acc[key] = uniq([...storeEvents.map(({ id }) => id), ...apiEvents.map(({ _id }) => _id)])
          .map((key) =>
            storeEvents.findIndex(({ id }) => id === key) !== -1
              ? storeEvents.find(({ id }) => id === key)
              : apiEvents.find(({ _id }) => _id === key),
          )
          .sort((a, b) => parseInt(a.metadata?.trackNumber) - parseInt(b.metadata?.trackNumber));

        if (acc[key].length > 1) {
          let accLength = acc[key].length;
          const firstDate = acc[key][0].date
            ? new Date(acc[key][0].date).getTime() / 1000
            : acc[key][0].startTime;
          let lastDate = acc[key][accLength - 1].date
            ? new Date(acc[key][accLength - 1].date).getTime() / 1000
            : acc[key][accLength - 1].startTime;
          while (firstDate > lastDate && accLength > 1) {
            acc[key].pop();
            accLength--;
            if (accLength > 1) {
              lastDate = acc[key][accLength - 1].date
                ? new Date(acc[key][accLength - 1].date).getTime() / 1000
                : acc[key][accLength - 1].startTime;
            }
          }
        }

        const idTournament = acc[key][0].tournament
          ? acc[key][0].tournament._id
          : acc[key][0].pid.split('.')[2];
        const nextDayEvents =
          nextEventsById[idTournament]?.filter(
            ({ metadata }) =>
              parseInt(metadata.trackNumber) >
              parseInt(acc[key][acc[key].length - 1].metadata.trackNumber),
          ) || [];

        if (`${acc[key][0].metadata.trackNumber}` !== '1') {
          const prevEvents =
            prevApiEventsById[idTournament]?.filter(
              (ev) =>
                parseInt(ev.metadata.trackNumber) < parseInt(acc[key][0].metadata.trackNumber),
            ) || [];
          acc[key].push(...prevEvents);
        }
        acc[key].push(...nextDayEvents);
        return acc;
      }, {});

      const allEvents = Object.values(eventsId).reduce((acc, ev) => {
        acc.push(...ev);
        return acc;
      }, []);

      const scheduleEventsByTournaments = this.sortTournaments(
        Object.values(this.getScheduleEventsByTournaments(allEvents)),
      );

      runInAction(() => {
        this.scheduleEvents = scheduleEventsByTournaments;
        this.isScheduleEventsLoading = false;
      });
    } catch (error) {
      logger.error('getScheduleEvents error', {
        error,
      });

      runInAction(() => {
        this.isScheduleEventsLoading = false;
      });
      if (fetchAttemptNum <= attemptsMaxNum) {
        await this.setScheduleEventsByDate(date, fetchAttemptNum + 1, tournamentId);
      }
    }
  };

  setWorldCupFinishedEvents = async () => {
    const { language } = this.root.ui;

    try {
      const {
        data: { data: eventsData },
      } = await getStatisticsEventsByDid({
        dateFrom: new Date(2024, 5, 14).getTime(),
        dateTo: new Date(2024, 6, 15).getTime(),
        disciplineId: 'football',
        locale: language,
        tournamentId: customTournamentPageId,
      });

      runInAction(() => {
        this.worldCupFinishedEvents = eventsData;
      });
    } catch (error) {
      logger.error('setWorldCupFinishedEvents error', {
        error,
      });
    }
  };

  get tournamentsByTypes() {
    const {
      lobbyFilters: { disciplines },
    } = this.root.ui;
    const [disciplineId] = disciplines;

    const tournamentsByTypes = this.tournamentsValues.reduce(
      (acc, tournament) => {
        const { disciplineId: did, lid, eventIds, sortIndex } = tournament;
        const [line] = lid.split('.');
        const [eventId] = eventIds;

        if (eventId) {
          if (eventId.includes('outright')) return acc;
        }

        const region = tournament.region || {};
        const { name, id } = region;

        if (did !== disciplineId || topEventsLine.includes(line)) {
          return acc;
        }

        if (sortIndex >= 50) {
          acc.top.push(tournament);
        }

        // International tournaments
        if (internationalTournamentsIds.includes(id)) {
          acc.international = [...acc.international, tournament];
          return acc;
        }
        // A-Z tournaments
        if (!name) {
          acc.unnamed = [...acc.unnamed, tournament];
        } else {
          if (!acc.regionsById[id]) {
            acc.regionsById[id] = region;
          }
          if (!acc.countriesIdsByName[name]) {
            acc.countriesIdsByName[name] = id;
          }
          if (!acc.tournamentsByCountries[name]) {
            acc.tournamentsByCountries[name] = [tournament];
          } else {
            acc.tournamentsByCountries[name] = [...acc.tournamentsByCountries[name], tournament];
          }
        }

        return acc;
      },
      {
        countriesIdsByName: {},
        international: [],
        regionsById: {},
        top: [],
        tournamentsByCountries: {},
        unnamed: [],
      },
    );

    const { countriesIdsByName } = tournamentsByTypes;

    tournamentsByTypes.countriesNames = Object.keys(countriesIdsByName).sort((a, b) =>
      a.localeCompare(b),
    );

    return tournamentsByTypes;
  }

  /**
   * Current discipline tournaments sorted by countries
   *
   * @returns {{[country: string]: CountryTournaments}}
   */
  get tournamentsByCountries() {
    const {
      lobbyFilters: { disciplines },
    } = this.root.ui;
    const [did] = disciplines;
    const tournamentsByDiscipline = this.lobbyTournamentsByDisciplines[did];

    if (!tournamentsByDiscipline) {
      return { notReady: true };
    }

    const topCountryByCid = topCountriesByCids.find(({ brand }) => brand === this.root.auth.brand);

    const { selectedCountry, otherCountries } = tournamentsByDiscipline.reduce(
      (acc, el) => {
        /** @type {CountryTournamentsItem} */
        const tournament = {
          ...el,
          countryName: '',
          racesNumbers: [],
        };

        const { eventRefs } = tournament;
        const countryName = tournament?.region?.name || '';
        const countrySlug = getFormattedSlug(countryName);
        const countryCode = tournament?.region?.code?.toUpperCase() || '';
        const racesNumbers = [];

        eventRefs.forEach((event) => {
          const trackNumber = event?.trackNumber;
          if (trackNumber) {
            racesNumbers.push(trackNumber);
          }
        });

        racesNumbers.sort((a, b) => a - b);

        tournament.countryName = countryName;
        tournament.racesNumbers = racesNumbers;

        if (topCountryByCid && topCountryByCid.countriesCode.includes(countryCode)) {
          if (acc.selectedCountry[countrySlug]) {
            acc.selectedCountry[countrySlug].tournaments.push(tournament);
          } else {
            acc.selectedCountry[countrySlug] = {
              countryName,
              region: tournament.region,
              tournaments: [tournament],
            };
          }
        } else if (acc.otherCountries[countrySlug]) {
          acc.otherCountries[countrySlug].tournaments.push(tournament);
        } else {
          acc.otherCountries[countrySlug] = {
            countryName,
            region: tournament.region,
            tournaments: [tournament],
          };
        }
        return acc;
      },
      {
        otherCountries: /** @type {{[country: string]: CountryTournaments}} */ ({}),
        selectedCountry: /** @type {{[country: string]: CountryTournaments}} */ ({}),
      },
    );

    const sortedCountriesNames = Object.keys(otherCountries).sort((a, b) => a.localeCompare(b));

    const sortedCountries = sortedCountriesNames.reduce(
      (acc, countryName) => {
        acc[countryName] = otherCountries[countryName];
        return acc;
      },
      /** @type {{[country: string]: CountryTournaments}} */ ({}),
    );

    const tournamentsByCountries = { ...selectedCountry, ...sortedCountries };
    Object.values(tournamentsByCountries).forEach(({ tournaments }) =>
      tournaments.sort((a, b) => a.eventRefs[0]?.startTime - b.eventRefs[0]?.startTime),
    );

    return tournamentsByCountries;
  }

  /**
   * Current discipline today tournaments sorted by countries
   *
   * @returns {{[country: string]: CountryTournaments}}
   */
  get tournamentsByCountriesToday() {
    // TODO not used anywere, double check and delete it.
    if (this.tournamentsByCountries.notReady) {
      return this.tournamentsByCountries;
    }

    return Object.entries(this.tournamentsByCountries).reduce(
      (acc, [countrySlug, tournament]) => {
        const tournamentData = { ...tournament };
        const { tournaments } = tournamentData;

        const todayTournaments = tournaments.filter((tournament) => {
          const todayTournamentEventsNum = tournament.eventIds.reduce((acc, id) => {
            const todayEventsNum = this.eventsValues.reduce(
              (acc, event) =>
                event.lid === id && isToday({ startTime: event.startTime }) ? acc + 1 : acc,
              0,
            );
            return acc + todayEventsNum;
          }, 0);
          if (todayTournamentEventsNum) {
            tournament.todayEventsNum = todayTournamentEventsNum;
            return tournament;
          }
        });

        if (todayTournaments.length) {
          tournamentData.tournaments = todayTournaments;
          return { ...acc, [countrySlug]: tournamentData };
        }

        return acc;
      },
      /** @type {{[country: string]: CountryTournaments}} */ ({}),
    );
  }

  /**
   * Events sorted by tournaments
   *
   * @returns {{[tournamentId: string]: {lid: string; startTime: number}[]}}
   */
  get cascadeEvents() {
    /** @type {ReadonlyArray<EN.EventRef & {pid: string}>} */
    if (!this.eventRefs.length) {
      return {};
    }

    const {
      lobbyFilters: { lines, date, disciplines },
    } = this.root.ui;

    const [currentDiscipline] = disciplines;

    const events = this.eventRefs.reduce(
      (items, evt) => {
        const { pid, startTime, type } = evt;
        const [line, discipline, tournament] = pid.split('.');
        if (discipline !== currentDiscipline) {
          return items;
        }
        if (!(lines.includes('outright') || lines.includes('Calendar')) && type === 1) {
          return items;
        }
        if (!lines.includes(line) || (lines.includes('outright') && type !== 1)) {
          return items;
        }
        if (date) {
          const dateString = Date.parse(date);
          const { dateFull: dateFilter } = dateHelper(dateString / 1000);
          const { dateFull } = dateHelper(startTime);
          if (dateFull !== dateFilter) {
            return items;
          }
        }
        if (!items[tournament]) {
          items[tournament] = [];
        }

        const index = items[tournament].findIndex((item) => evt.startTime < item.startTime);

        if (index < 0) {
          items[tournament].push(evt);
        } else {
          items[tournament].splice(index, 0, evt);
        }

        return items;
      },
      /** @type {{[tournamentId: string]: {lid: string; startTime: number}[]}} */ ({}),
    );

    const tournamentsByDid = this.lobbyTournamentsByDisciplines[currentDiscipline] || [];

    const tournamentsIds = tournamentsByDid.map(({ id }) => id);

    return Object.keys(events)
      .sort((a, b) => tournamentsIds.indexOf(a) - tournamentsIds.indexOf(b))
      .reduce(
        (acc, item) => {
          acc[item] = events[item];

          return acc;
        },
        /** @type {{[tournamentId: string]: {lid: string; startTime: number}[]}} */ ({}),
      );
  }

  get gridEventsLive() {
    if (!this.eventRefs.length) {
      return [];
    }
    const {
      lobbyFilters: { date, disciplines, country },
    } = this.root.ui;

    return this.eventRefs
      .reduce((items, event) => {
        const { pid, startTime } = event;
        const [line, discipline, tournamentId] = pid.split('.');

        if (line !== '2') {
          return items;
        }

        if (
          !(
            this.actualDisciplines.includes(discipline) ||
            disciplines.includes(customTournamentPageSlug)
          )
        ) {
          return items;
        }

        // stub for football world cup
        if (
          customTournamentPageId &&
          disciplines.includes(customTournamentPageSlug) &&
          tournamentId !== customTournamentPageId
        ) {
          return items;
        }

        if (date) {
          const dateString = Date.parse(date);
          const { dateFull: dateFilter } = dateHelper(dateString / 1000);
          const { dateFull } = dateHelper(startTime);
          if (dateFull !== dateFilter) {
            return items;
          }
        }

        if (country) {
          const { id: regionId } = this.tournaments.get(pid)?.region || { id: null };

          if (regionId !== country) {
            return items;
          }
        }

        items.push(event);

        return items;
      }, [])
      .sort((a, b) => a.startTime - b.startTime);
  }

  get gridEventsPrematch() {
    const eventsLength = this.eventRefs.length;

    if (!eventsLength) {
      return [];
    }

    const { lobbyFilters } = this.root.ui;
    const { lines, date, disciplines, country } = lobbyFilters;

    const events = this.eventRefs.reduce((items, evt, i) => {
      const { lid, pid, startTime, type } = evt;

      const [line, discipline, tournamentId] = pid.split('.');

      const event = { lid, pid, startTime, type };

      if (!(lines.includes('outright') || lines.includes('Calendar')) && type === 1) {
        return items;
      }
      if (line !== '1') {
        return items;
      }

      if (lines.includes('willBeLive') && !evt.willBeLive) {
        return items;
      }

      if (
        !(
          this.actualDisciplines.includes(discipline) ||
          disciplines.includes(customTournamentPageSlug)
        )
      ) {
        return items;
      }

      // stub for football world cup
      const isCustomTournamentPage =
        tournamentId === customTournamentPageId ||
        customTournamentPageOutrightIds.includes(tournamentId);

      if (
        customTournamentPageId &&
        disciplines.includes(customTournamentPageSlug) &&
        !isCustomTournamentPage
      ) {
        return items;
      }

      if (date) {
        const dateString = Date.parse(date);
        const { dateFull: dateFilter } = dateHelper(dateString / 1000);
        const { dateFull } = dateHelper(startTime);
        if (dateFull !== dateFilter) {
          return items;
        }
      }

      if (country) {
        const { id: regionId } = this.tournaments.get(pid)?.region || { id: null };

        if (regionId !== country) {
          return items;
        }
      }

      const dateOrder = new Date(startTime * 1000);

      // A gap has been added to ensure that live events are always displayed first on the discipline page,
      // even if the start date of the prematch event is earlier than today
      // @/pages/LobbyPages/DisciplinePage/LineByTime/LineByTime.jsx:38
      const gap = 10000;
      const order = dateDiffInDays(new Date(), dateOrder) + gap;

      const group = items[order] || [];
      if (isCustomTournamentPage) {
        event.eventId = tournamentId;
      }
      group.push(event);

      items[order] = group;

      return items;
    }, {});

    Object.values(events).forEach((events) => {
      events.sort((a, b) => a.startTime - b.startTime);
    });

    return events;
  }

  get gridEventsOutright() {
    return Object.entries(this.gridEventsPrematch).reduce((acc, [k, v]) => {
      const arr = v.filter((el) => el.type === 1);
      if (arr.length) {
        const res = { [k]: arr };
        return { ...acc, ...res };
      }
      return acc;
    }, {});
  }

  get gridEventsPrematchIds() {
    return Object.keys(this.gridEventsPrematch);
  }

  get gridEventsTitlesIds() {
    return sortBy(
      this.gridEventsPrematchIds.map((key) => {
        const { startTime } = this.gridEventsPrematch[key][0];
        return { id: key, startTime };
      }),
      'startTime',
    );
  }

  get eventsDatesByDisciplines() {
    if (!this.eventRefs.length) {
      return {};
    }
    const {
      lobbyFilters: { country: countryFilter },
    } = this.root.ui;

    let tournamentsByRegion = [];

    if (countryFilter) {
      const { regionsById, tournamentsByCountries } = this.tournamentsByTypes;
      const { name } = regionsById[countryFilter] || {};
      tournamentsByRegion = tournamentsByCountries[name] || [];
    }

    return this.eventRefs.reduce((acc, event) => {
      const { startTime, type, pid } = event;
      const [_, did] = pid.split('.');

      if (countryFilter) {
        const hasTournamentsByRegion = tournamentsByRegion.some(({ lid }) => pid === lid);
        if (!hasTournamentsByRegion) {
          return acc;
        }
      }

      if (!acc[did]) {
        acc[did] = new Set();
      }

      const { dateFull } = dateHelper(startTime);
      acc[did].add(dateFull);
      return acc;
    }, {});
  }

  get sortedTopEvents() {
    const { isAnimalsRacingView } = this.root.ui;

    return this.eventsValues
      .filter((event) => {
        const [line, _] = event.pid.split('.');
        if (isAnimalsRacingView) {
          return line === '1';
        }
        return line === '5' || line === '4';
      })
      .sort((a, b) => b.topIndex - a.topIndex);
  }

  get sortedTopEventsRefs() {
    const { isAnimalsRacingView } = this.root.ui || {};

    return this.eventRefs.filter(({ pid, type }) => {
      if (type === 1) {
        return false;
      }
      const [line] = pid.split('.');
      if (isAnimalsRacingView) {
        return line === '1';
      }
      return line === '5' || line === '4';
    });
  }

  get justForYouBlockEvents() {
    const linesSlugs = { 4: 'priorityPrematch', 5: 'priorityLive' };
    const eventRefsLength = this.eventRefs.length;
    let size = 0;

    return this.eventRefs.reduce(
      (acc, eventRef, i) => {
        const { type, pid } = eventRef;
        const [line, _, tournamentId] = pid.split('.');

        if (type !== 1 && topEventsLines.has(line)) {
          if (justForYouBlockTournamentIds.has(tournamentId)) {
            acc[linesSlugs[line]].push(eventRef);
          } else {
            acc[line].push(eventRef);
          }
          size += 1;
        }

        if (i + 1 === eventRefsLength) {
          Object.values(acc).forEach((events) => {
            events.sort((a, b) => a.startTime - b.startTime);
          });
          acc.size = size;
        }

        return acc;
      },
      { 4: [], 5: [], priorityLive: [], priorityPrematch: [] },
    );
  }

  get topEventsByDisciplines() {
    const eventsLength = this.sortedTopEventsRefs.length;

    if (!eventsLength) {
      return {};
    }

    return this.sortedTopEventsRefs.reduce((acc, event, i) => {
      const [line, discipline] = event.pid.split('.');

      if (!acc[discipline]) {
        acc[discipline] = { 1: [], 4: [], 5: [] };
      }
      acc[discipline][line].push(event);
      if (i + 1 === eventsLength) {
        Object.values(acc).forEach((obj) => {
          Object.values(obj).forEach((events) => {
            events.sort((a, b) => b.topIndex - a.topIndex);
          });
        });
      }
      return acc;
    }, {});
  }

  get topDisciplines() {
    const { disciplineIds, isAnimalsRacingView } = this.root.ui;

    const events = Object.keys(this.topEventsByDisciplines)
      .filter((id) => disciplineIds.some((el) => el === id))
      .sort((a, b) => disciplineIds.indexOf(a) - disciplineIds.indexOf(b));

    if (isAnimalsRacingView) {
      return events.map((id) => this.disciplines.get(`1.${id}`)).filter(Boolean);
    }

    return events
      .map((id) => this.disciplines.get(`4.${id}`) || this.disciplines.get(`5.${id}`))
      .filter(Boolean);
  }

  get topDisciplinesValues() {
    if (!this.disciplinesValues.length) return [];
    const { disciplineIds } = this.root.ui;

    const disciplines = this.disciplinesValues.filter(({ pid, id: disciplineId }, i, arr) => {
      if (disciplineIds.some((el) => el === disciplineId)) {
        return pid === '4' || pid === '5';
      }
    });

    return uniqBy(disciplines, 'id').sort(
      (a, b) => disciplineIds.indexOf(a.id) - disciplineIds.indexOf(b.id),
    );
  }

  get liveDisciplines() {
    if (!this.disciplinesValues.length) return [];
    const { disciplineIds } = this.root.ui;
    return this.disciplinesValues
      .filter(({ pid, id }) => pid === '2' && disciplineIds.some((el) => el === id))
      .sort((a, b) => disciplineIds.indexOf(a.id) - disciplineIds.indexOf(b.id));
  }

  /**
   * Top disciplines with events for LiveTopEventsWidget
   *
   * @returns {TopDisciplineWithEvents[]}
   */
  get topDisciplinesWithEvents() {
    if (!this.disciplinesValues.length) return [];

    const disciplinesSorted = this.activeTopDisciplinesFilters.length
      ? this.topDisciplinesValues.filter(({ id }) => this.activeTopDisciplinesFilters.includes(id))
      : this.topDisciplinesValues;

    return disciplinesSorted.reduce(
      (acc, discipline) => {
        const { name, id: disciplineId } = discipline;

        const eventsByTopIndex = this.sortedTopEvents.reduce(
          (eventsAcc, event) => {
            const { topIndex, did } = event;

            const currentTopIndex = topIndex || 0;

            if (did !== disciplineId) {
              return eventsAcc;
            }

            if (eventsAcc[currentTopIndex]) {
              eventsAcc[currentTopIndex].push(event);
            } else {
              eventsAcc[currentTopIndex] = [event];
            }

            return eventsAcc;
          },
          /** @type {{[topIndex: string]: Event[]}} */ ({}),
        );

        const eventsIndexes = Object.keys(eventsByTopIndex);

        if (!eventsIndexes.length) {
          return acc;
        }

        const disciplineEvents = [];
        eventsIndexes.reverse().forEach((topIndex) => {
          const sortedByTime = eventsByTopIndex[topIndex].sort((a, b) => a.startTime - b.startTime);
          sortedByTime.forEach((el) => disciplineEvents.push(el));
        });

        acc.push({
          events: disciplineEvents,
          id: disciplineId,
          name,
        });

        return acc;
      },
      /** @type {TopDisciplineWithEvents[]} */ ([]),
    );
  }

  /**
   * Live disciplines with events
   *
   * @returns {LiveDisciplineWithEvents[]}
   */
  get liveDisciplinesWithEvents() {
    if (!this.disciplinesValues.length) return [];

    const disciplinesSorted = this.activeLiveFilters.length
      ? this.liveDisciplines.filter(({ id }) => this.activeLiveFilters.includes(id))
      : this.liveDisciplines;

    return disciplinesSorted.reduce(
      (acc, discipline) => {
        const { name, id } = discipline;

        const eventsByTopIndex = this.eventRefs.reduce(
          (eventsAcc, event) => {
            const { topIndex, pid } = event;
            const [line, disciplineId] = pid.split('.');
            const currentTopIndex = topIndex || 0;

            if (discipline.id !== disciplineId || line !== '2') {
              return eventsAcc;
            }

            if (eventsAcc[currentTopIndex]) {
              eventsAcc[currentTopIndex].push(event);
            } else {
              eventsAcc[currentTopIndex] = [event];
            }

            return eventsAcc;
          },
          /** @type {{[topIndex: string]: Event[]}} */ ({}),
        );

        const eventsIndexes = Object.keys(eventsByTopIndex);

        if (!eventsIndexes.length) {
          return acc;
        }

        const disciplineEvents = [];
        eventsIndexes.reverse().forEach((topIndex) => {
          const sortedByTime = eventsByTopIndex[topIndex].sort((a, b) => a.startTime - b.startTime);
          sortedByTime.forEach((el) => disciplineEvents.push(el));
        });

        acc.push({
          events: disciplineEvents,
          id,
          name,
        });

        return acc;
      },
      /** @type {LiveDisciplineWithEvents[]} */ ([]),
    );
  }

  get widgetLiveDisciplinesWithEvents() {
    if (!this.disciplinesValues.length) return [];

    const disciplinesSorted = this.activeLiveFilters.length
      ? this.liveDisciplines.filter(({ id }) => this.activeLiveFilters.includes(id))
      : this.liveDisciplines;

    return disciplinesSorted.reduce(
      (acc, discipline) => {
        const { name, id: did } = discipline;

        const eventsByTopIndex = this.liveEvents.reduce(
          (eventsAcc, event) => {
            const { topIndex, pid } = event;
            const [line, disciplineId] = pid.split('.');
            const currentTopIndex = topIndex || 0;

            if (did !== disciplineId || line !== '2') {
              return eventsAcc;
            }

            if (eventsAcc[currentTopIndex]) {
              eventsAcc[currentTopIndex].push(event);
            } else {
              eventsAcc[currentTopIndex] = [event];
            }

            return eventsAcc;
          },
          /** @type {{[topIndex: string]: Event[]}} */ ({}),
        );

        const eventsIndexes = Object.keys(eventsByTopIndex);

        if (!eventsIndexes.length) {
          return acc;
        }

        const disciplineEvents = [];
        eventsIndexes.reverse().forEach((topIndex) => {
          const sortedByTime = eventsByTopIndex[topIndex].sort((a, b) => a.startTime - b.startTime);
          sortedByTime.forEach((el) => disciplineEvents.push(el));
        });

        acc.push({
          events: disciplineEvents,
          id: did,
          name,
        });

        return acc;
      },
      /** @type {LiveDisciplineWithEvents[]} */ ([]),
    );
  }

  get activeTopDisciplinesFilters() {
    return this.topDisciplinesValues
      .filter(({ id }) => this.topDisciplinesFilters.includes(id))
      .map(({ id }) => id);
  }

  get activeLiveFilters() {
    return this.liveDisciplines
      .filter(({ id }) => this.liveDisciplinesFilters.includes(id))
      .map(({ id }) => id);
  }

  get sc() {
    return (
      this.lines.size +
      this.disciplines.size +
      this.tournaments.size +
      this.events.size +
      this.markets.size +
      this.scoreboards.size
    );
  }

  bulkAdd = (name, items) => {
    const model = getName(name);
    console.log('applySnapshot', this[model], items);
  };

  setTimestamp(id) {
    window.timestamp = id;
  }

  add = (name, newItem) => {
    const { lid } = newItem;
    const model = getName(name);
    const collection = this[model];
    collection.set(lid, newItem);
  };

  /**
   * @private
   *
   * @param {string} model
   * @param {string} nextLid
   */
  handleActiveEvent = (model, nextLid) => {
    if (model === 'event' && this.activeEventSelected && this.activeEventLids[nextLid]) {
      const { lid: prevLid } = this.activeEventData;

      if (prevLid) {
        const [currentEventLine] = prevLid.split('.');
        const currentEventIsLive = liveFullLine.includes(currentEventLine);

        // do not allow event to transition backwards from live
        if (currentEventIsLive && nextLid !== prevLid) {
          return;
        }
      }

      this.setActiveEventData(this.events.get(nextLid) || {});
    }
  };

  /**
   * Handles incoming data for the "Popular Bets" widget and updates the corresponding state.
   *
   * @action
   * @param {number} action - The action type received from the socket, corresponding to `socketBatchActions`.
   * @param {Object} data - The data payload containing information about outcomes.
   *
   * - If the `action` is `DELETE`, it clears the "Popular Bets" state.
   * - Otherwise, filters outcomes based on settled bets and updates the state.
   */
  handlePopularBetsWidgetData = (action, data) => {
    if (action === socketBatchActions.DELETE) {
      this.root.betslip.setPopularBets([]);
      return;
    }

    const settledBets = this.root.betslip.handleSettledBetsByLS({
      lsKey: 'settledPopularBets',
    });

    this.root.betslip.setPopularBets(
      data.outcomes.filter(({ outcomeId }) => !settledBets[outcomeId]),
    );
  };

  setJustForYouEvents = (data) => {
    this.justForYouEvents = data;
  };

  handleJustForYouWidgetData = (data) => {
    const justForYouEvents = [];
    data.events.forEach((event) => {
      justForYouEvents.push({
        ...event.event,
        disciplineId: event.tournament.disciplineId,
        logo: event.tournament.logo,
        logoExists: event.tournament.logoExists,
        tournamentId: event.tournament.id,
        tournamentName: event.tournament.name,
      });
    });

    this.setJustForYouEvents(justForYouEvents);
  };

  addBunch = (payload) => {
    const { disciplineIdsAliases } = this.root.ui;
    runInAction(() => {
      payload.forEach(({ model, action, value: data }) => {
        const id = data.lid;
        if (!entityNames[model]) {
          if (model === 'widget') {
            this.handleWidgetsSubscription(id, action);

            switch (id) {
              case 'popularBets':
                this.handlePopularBetsWidgetData(action, data);
                break;
              case 'justForYou':
                this.handleJustForYouWidgetData(data);
                break;
            }
          }

          if (model === 'count') {
            this.setCounts(data);
          }

          return;
        }

        const storeName = `${model}s`;
        const currentModel = this[storeName].get(id);

        if (model === 'market') {
          const [line, rawEventId, rawMarketId] = id.split('.');
          const eventLid = `${line}.${rawEventId}`;
          const marketId = id;

          // if (action === socketBatchActions.UPDATE) {
          //   const hasEvent = this.events.has(eventLid);
          //   if (!hasEvent) {
          //     Socket.unsubscribe(model, [id]);
          //     this.markets.delete(id);
          //     return;
          //   }
          // }

          // update bet data in betslip if prematch market was change state to live
          if (this.root.betslip.marketIdsFromBetSlip[marketId]) {
            const [line, eventId] = (eventLid || '').split('.');

            if (prematchFullLine.includes(line)) {
              const betIdsToSubscribe = this.root.betslip.bets.reduce(
                (acc, { pid, id: outcomeId }) => {
                  if (pid === id) {
                    acc[outcomeId] = `2.${eventId}.${marketId}`;
                  }
                  return acc;
                },
                {},
              );

              this.root.betslip
                .subscribeOnMarketsInSW({ marketsPids: betIdsToSubscribe })
                .then((res) => {
                  if (res) {
                    res.resolved.forEach(({ outcomes }) => {
                      outcomes.forEach((outcome) => {
                        const currentBet = this.root.betslip.betsFromBetSlipByIds[outcome.id];
                        if (currentBet) {
                          Object.entries(outcome).forEach(([k, v]) => {
                            currentBet.updateProp(k, v);
                          });
                          currentBet.updateProp('pidLid', outcome.pid);

                          this.root.betslip.addToStorage(currentBet);
                          this.root.betslip.filterStorage();
                        }
                      });
                    });
                  }
                });
            }
          }

          const market = this.markets.get(id);

          if (this.activeEventSelected && market) {
            if (action === socketBatchActions.DELETE) {
              if (this.activeEventLids[eventLid]) {
                market.outcomes.forEach((el) => {
                  // we will reset this back to correct value
                  // in case we get an update for this market
                  el.isFrozen = true;
                });

                market.deletionTimeOut = setTimeout(() => this[storeName].delete(id), 3000);
                return;
              }
            } else if (market.deletionTimeOut) {
              clearTimeout(market.deletionTimeOut);
              market.deletionTimeOut = null;
            }
          }
        }

        if (action === socketBatchActions.DELETE) {
          if (this.activeEventSelected) {
            const activeEventLid = this.activeEventLids[id];
            if (
              (model === 'tournament' && data.lid === this.events.get(activeEventLid)?.pid) ||
              (model === 'event' && id === activeEventLid)
            ) {
              this.setActiveEventIsOver(true);
              return;
            }
          }

          this[storeName].delete(id);
          return;
        }

        if (model === 'scoreboard') {
          const event = this.events.get(id);
          if (event) {
            const { scoreboard, timer, stage, server } = /** @type {EN.Scoreboard} */ (data);
            this.stages.set(id, stage);
            this.timers.set(id, timer);
            if (server) {
              this.servings.set(id, server);
            }
            this.scoreboards.set(id, scoreboard);
          } else {
            // Socket.unsubscribe(model, [id]);
            // this.scoreboards.delete(id);
          }

          return;
        }

        if (model === 'discipline') {
          const {
            id: dId,
            tournamentIds: dTournamentIds,
            slug: dSlug,
          } = /** @type {EN.Discipline} */ (data);

          const alias = disciplineIdsAliases[dId];

          if (dId !== alias) {
            if (!alias) {
              return;
            }
            const allowedLId = id.replace(dId, alias);
            const stored = this[storeName].get(allowedLId);

            if (action === socketBatchActions.CREATE && stored && stored.mergedEntityChildIds) {
              return;
            }
            const allowedTournamentIds = dTournamentIds.map((id) => id.replace(dId, alias));
            if (stored) {
              const storedTournamentIds = stored.tournamentIds;
              const uniqTournamentIds = uniq([...storedTournamentIds, ...allowedTournamentIds]);
              let idsToWrite = [...storedTournamentIds];

              if (JSON.stringify(storedTournamentIds) !== JSON.stringify(uniqTournamentIds)) {
                idsToWrite = uniqTournamentIds;
              }

              this[storeName].set(allowedLId, {
                ...stored,
                mergedEntityChildIds: allowedTournamentIds,
                tournamentIds: idsToWrite,
              });
            } else {
              this[storeName].set(
                allowedLId,
                new models[storeName]({
                  ...data,
                  id: alias,
                  lid: allowedLId,
                  name: this.counts[alias]?.name,
                  shortName: this.counts[alias]?.name,
                  slug: dSlug.replace(dId, alias),
                  tournamentIds: allowedTournamentIds,
                }),
              );
            }
            return;
          }
          if (action === socketBatchActions.UPDATE && currentModel) {
            const childEntityIdsName = subscribeChain[model]?.nextModelIds;

            for (const [k, v] of Object.entries(data)) {
              if (
                childEntityIdsName &&
                currentModel.mergedEntityChildIds &&
                childEntityIdsName === k
              ) {
                const mergedIds = uniq([...v, ...currentModel.mergedEntityChildIds]);

                currentModel[k] = mergedIds;
                continue;
              }
              currentModel[k] = v;
            }
            return;
          }
        }

        if (model === 'tournament') {
          const {
            disciplineId,
            pid: tPid,
            slug: tSlug,
            eventIds,
          } = /** @type {EN.Tournament} */ (data);

          if (this.activeEventSelected && eventIds.some((id) => this.activeEventLids[id])) {
            this.setActiveEventIsOver(false);
          }

          const alias = disciplineIdsAliases[disciplineId];
          if (disciplineId !== alias) {
            if (!alias) {
              return;
            }
            const allowedLId = id.replace(disciplineId, alias);

            this[storeName].set(
              allowedLId,
              new models[storeName]({
                ...data,
                disciplineId: alias,
                lid: allowedLId,
                pid: tPid.replace(disciplineId, alias),
                slug: tSlug.replace(disciplineId, alias),
              }),
            );

            return;
          }
        }

        if (model === 'event') {
          const { did, pid: ePid, slug: eSlug } = /** @type {EN.Event} */ (data);
          const alias = disciplineIdsAliases[did];

          if (this.activeEventSelected && this.activeEventLids[id]) {
            this.setActiveEventIsOver(false);
          }

          if (did !== alias) {
            if (!alias) {
              return;
            }
            this[storeName].set(
              id,
              new models[storeName]({
                ...data,
                did: alias,
                pid: ePid.replace(did, alias),
                slug: eSlug.replace(did, alias),
              }),
            );

            this.handleActiveEvent(model, id);

            return;
          }
        }

        switch (action) {
          case socketBatchActions.CREATE:
            this[storeName].set(id, new models[storeName](data));
            break;
          case socketBatchActions.UPDATE:
            if (currentModel) {
              currentModel.update(data);
            } else {
              logger.warn(`Unexpected ${storeName} update`, {
                action,
                data,
              });
            }
            break;
        }

        this.handleActiveEvent(model, id);
      });
    });
  };

  update = (name, id, newItem) => {
    const model = getName(name);

    const item = new models[model](newItem);
    this[model].set(id, item);
  };

  delete = (name, id) => {
    const model = getName(name);
    const item = this[model].get(id);

    if (item) {
      if (name === 'event') {
        item.marketIds.forEach((m) => {
          if (!item.mainMarketIds.includes(m)) {
            if (m.lid) {
              item.mainMarketIds.remove(m);
            }
          }
        });
      }
      this[model].delete(id);
    }
  };

  clear = () => {
    this.counts = {};
    this.scoreboards.clear();
    Object.keys(models).forEach((name) => this[name].clear());
  };

  updateOutcomeById = (outcomeId, marketId, propName, value) => {
    if (!outcomeId || !marketId) return;
    const outcome = this.getOutcomeByLID(outcomeId, marketId);
    if (outcome) {
      outcome[propName] = value;
    } else {
      console.warn(`Outcome with id ${outcomeId} not exist`);
    }
  };

  setScheduleDisciplines = (disciplines = []) => {
    this.scheduleDisciplines = disciplines;
  };

  addScoreboardSub = (lid) => {
    this.scoreboardSubs = [...this.scoreboardSubs, lid];
  };

  deleteScoreboardSub = (lid) => {
    this.scoreboardSubs = [...this.scoreboardSubs.filter((id) => id !== lid)];
  };

  setCounts = (payload) => {
    this.counts = { ...this.counts, [payload.key]: payload };
  };

  toggleLiveDisciplineFilter = (id, isSelectByOne) => {
    if (isSelectByOne) {
      this.liveDisciplinesFilters = [id];
      return;
    }
    if (this.liveDisciplinesFilters.includes(id)) {
      this.liveDisciplinesFilters = this.liveDisciplinesFilters.filter(
        (discipline) => discipline !== id,
      );
    } else {
      this.liveDisciplinesFilters.push(id);
    }
    LS.set('liveDisciplinesFilters', JSON.stringify(this.liveDisciplinesFilters));
  };

  toggleTopPageDisciplineFilter = (id) => {
    if (this.topPageDisciplinesFilters.includes(id)) {
      this.topPageDisciplinesFilters = this.topPageDisciplinesFilters.filter(
        (discipline) => discipline !== id,
      );
    } else {
      this.topPageDisciplinesFilters = [...this.topPageDisciplinesFilters, id];
    }
    LS.set('TPDF', JSON.stringify(this.topPageDisciplinesFilters));
  };

  toggleTopDisciplinesFilter = (id) => {
    this.topDisciplinesFilters = [id];
  };

  resetLiveDisciplineFilter = () => {
    this.liveDisciplinesFilters = [];
    LS.remove('liveDisciplinesFilters');
  };

  setLazyParams = (
    lazyParams = {
      isSteppedGridEventsRender: false,
    },
  ) => {
    Object.entries(lazyParams).forEach(([param, val]) => {
      this[param] = val;
    });
  };

  setIsMarketsIntervalsMode = (bool = false) => {
    this.isMarketsIntervalsMode = bool;
  };

  /*
   * Fetches and returns market intervals for a given discipline ID.
   * Created for optimizing multiple simultaneous requests for the same data.
   */
  getMarketsIntervals = (disciplineId) => {
    if (!this.loadingMarketsIntervalPromise) {
      this.loadingMarketsIntervalPromise = this.loadIntervals(disciplineId).finally(() => {
        this.loadingMarketsIntervalPromise = null;
      });
    }

    return this.loadingMarketsIntervalPromise;
  };

  loadIntervals = async (disciplineId) => {
    if (this.marketsIntervals[disciplineId]) {
      return this.marketsIntervals[disciplineId];
    }

    const { language } = this.root.ui;

    try {
      const {
        data: { data: marketsIntervalsData },
      } = await getIntervalsByDiscipline(disciplineId, {
        locale: language,
      });

      if (marketsIntervalsData) {
        this.marketsIntervals[disciplineId] = marketsIntervalsData;

        return this.marketsIntervals[disciplineId];
      }
    } catch (error) {
      logger.error('getMarketsIntervals error', {
        error,
      });
    }

    return [];
  };

  /**
   * Handles widget subscription by adding or deleting widgets based on the action type.
   * @param {string} widgetId - The ID of the widget to manage.
   * @param {number} action - The action type (e.g., CREATE or DELETE).
   */

  // biome-ignore lint/correctness/noInvalidUseBeforeDeclaration: todo fix this
  handleWidgetsSubscription(widgetId, action) {
    if (action === socketBatchActions.DELETE) {
      this.subscribedWidgets.delete(widgetId);
    } else if (action === socketBatchActions.CREATE) {
      this.subscribedWidgets.add(widgetId);
    }
  }
}
